/**
 * BUSINESS LAYER (BL): PERSONAL QUICK REACTION (REACCIÓN RÁPIDA PERSONAL - HU-04)
 * Capa de lógica de negocio, validaciones defensivas de formulario de aficionados,
 * motor de audio sintetizado con Web Audio API (cero dependencias de archivos MP3),
 * gestión de Screen Wake Lock API con re-adquisición automática ante cambio de pestaña,
 * feedback háptico con navigator.vibrate, precarga de assets multimedia en segundo plano,
 * determinación de insignias oficiales por tiempo en ms y telemetría dual (Backend + GTM).
 * 
 * @module utilities/BL/PersonalQuickReaction
 */

import {
    GetPublicConfig as DLGetPublicConfig,
    RegisterParticipant as DLRegisterParticipant,
    SubmitPlay as DLSubmitPlay,
    TrackTelemetry as DLTrackTelemetry,
    GetRanking as DLGetRanking,
    GetWinner as DLGetWinner,
    GetAdminData as DLGetAdminData,
    UpdateAdminConfig as DLUpdateAdminConfig,
    UpdateAdminOverlay as DLUpdateAdminOverlay,
    UploadAsset as DLUploadAsset,
    GetThemes as DLGetThemes,
    SaveTheme as DLSaveTheme,
    ResetParticipant as DLResetParticipant,
    FullReset as DLFullReset
} from '../DL/PersonalQuickReaction';

// Clave única para almacenamiento local de sesión de participante
export const STORAGE_SESSION_KEY = 'interaktia_pqr_fan_session';
export const STORAGE_MUTE_KEY = 'interaktia_pqr_fan_muted';

// =========================================================================
// 1. SANEAMIENTO Y VALIDACIONES DE FORMULARIO
// =========================================================================

/**
 * Sanea texto de entrada para nombres o campos libres, eliminando caracteres de control.
 * 
 * @param {string} text - Texto a sanear.
 * @param {number} [maxLength=200] - Longitud máxima permitida.
 * @returns {string} Texto saneado.
 */
export const sanitizeInputText = (text, maxLength = 200) => {
    if (!text || typeof text !== 'string') return '';
    return text
        .replace(/[\r\n\t\0]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .substring(0, maxLength);
};

/**
 * Sanea un identificador GUID de sala.
 * 
 * @param {string|null} roomId - Identificador GUID.
 * @returns {string} RoomId saneado.
 */
export const sanitizeRoomId = (roomId) => {
    if (!roomId || typeof roomId !== 'string') return '';
    return roomId.replace(/[^a-zA-Z0-9\-]/g, '').trim().substring(0, 50);
};

/**
 * Valida el nombre del participante (mínimo 2 caracteres, máximo 200).
 * 
 * @param {string} name - Nombre completo o alias.
 * @returns {boolean} Verdadero si es válido.
 */
export const validateParticipantName = (name) => {
    if (!name || typeof name !== 'string') return false;
    const trimmed = name.trim();
    return trimmed.length >= 2 && trimmed.length <= 200;
};

/**
 * Valida formato de correo electrónico según estándar RFC 5322 simplificado.
 * 
 * @param {string} email - Correo electrónico.
 * @returns {boolean} Verdadero si cumple formato de correo.
 */
export const validateEmail = (email) => {
    if (!email || typeof email !== 'string') return false;
    const trimmed = email.trim().toLowerCase();
    const regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    return regex.test(trimmed) && trimmed.length <= 200;
};

/**
 * Valida número de teléfono (mínimo 7 caracteres numéricos o formateados, hasta 20).
 * 
 * @param {string} phone - Teléfono de contacto.
 * @returns {boolean} Verdadero si cumple longitud mínima.
 */
export const validatePhone = (phone) => {
    if (!phone || typeof phone !== 'string') return false;
    const cleanDigits = phone.replace(/\D/g, '');
    return cleanDigits.length >= 7 && cleanDigits.length <= 15;
};

/**
 * Valida que los Términos y Condiciones hayan sido aceptados explícitamente.
 * 
 * @param {boolean} acceptedTerms - Estado del checkbox.
 * @returns {boolean}
 */
export const validateTerms = (acceptedTerms) => {
    return Boolean(acceptedTerms === true);
};

/**
 * Valida que el Aviso de Privacidad haya sido aceptado explícitamente.
 * 
 * @param {boolean} acceptedPrivacy - Estado del checkbox.
 * @returns {boolean}
 */
export const validatePrivacy = (acceptedPrivacy) => {
    return Boolean(acceptedPrivacy === true);
};

/**
 * Valida de forma integral el formulario de registro de participante móvil.
 * 
 * @param {Object} formData - Objeto con participantName, email, phone, acceptedTerms, acceptedPrivacy.
 * @returns {{ isValid: boolean, errors: Object }} Resultado y mapa de errores por campo.
 */
export const validateRegistrationForm = (formData = {}) => {
    const errors = {};

    if (!validateParticipantName(formData.participantName)) {
        errors.participantName = 'Ingresa tu nombre (mínimo 2 caracteres).';
    }

    if (!validateEmail(formData.email)) {
        errors.email = 'Ingresa un correo electrónico válido (ej. usuario@correo.com).';
    }

    if (!validatePhone(formData.phone)) {
        errors.phone = 'Ingresa un teléfono válido (al menos 7 a 10 dígitos numéricos).';
    }

    if (!validateTerms(formData.acceptedTerms)) {
        errors.acceptedTerms = 'Debes aceptar los Términos y Condiciones para participar.';
    }

    if (!validatePrivacy(formData.acceptedPrivacy)) {
        errors.acceptedPrivacy = 'Debes aceptar la Política de Privacidad.';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// =========================================================================
// 2. MOTOR DE SÍNTESIS DE AUDIO CON WEB AUDIO API (ZERO MP3)
// =========================================================================

/**
 * Crea e inicializa una instancia del motor de síntesis de audio nativo con Web Audio API.
 * Proporciona beeps de conteo (440 Hz / 880 Hz), tono oscilante de tensión, trigger penetrante (1200 Hz),
 * acordes arpegiados de victoria, sonido disonante de falso inicio y control Mute/Unmute persistente.
 * 
 * @returns {Object} Instancia del motor de audio sintético.
 */
export const createSynthesizedAudioEngine = () => {
    let audioContext = null;
    let tensionOscillator = null;
    let tensionLfo = null;
    let tensionGainNode = null;
    let tensionFilterNode = null;

    let isMutedInternal = false;

    // Recuperar preferencia de silencio previa
    if (typeof sessionStorage !== 'undefined') {
        isMutedInternal = sessionStorage.getItem(STORAGE_MUTE_KEY) === 'true';
    }

    /**
     * Obtiene o instancia el AudioContext en el navegador.
     * @returns {AudioContext|null}
     */
    const getContext = () => {
        if (typeof window === 'undefined' && typeof global === 'undefined') return null;
        if (!audioContext) {
            const AudioContextClass = (typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null) ||
                (typeof global !== 'undefined' ? (global.AudioContext || global.webkitAudioContext) : null);
            if (AudioContextClass) {
                try {
                    audioContext = new AudioContextClass();
                } catch {
                    audioContext = null;
                }
            }
        }
        return audioContext;
    };

    /**
     * Reanuda y desbloquea el AudioContext ante el primer gesto del usuario.
     * @returns {Promise<boolean>}
     */
    const unlock = async () => {
        try {
            const ctx = getContext();
            if (!ctx) return false;
            if (ctx.state === 'suspended') {
                await ctx.resume();
            }
            return ctx.state === 'running';
        } catch {
            return false;
        }
    };

    /**
     * Establece el estado de silencio (Mute / Unmute).
     * @param {boolean} muted 
     */
    const setMuted = (muted) => {
        isMutedInternal = Boolean(muted);
        if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(STORAGE_MUTE_KEY, isMutedInternal ? 'true' : 'false');
        }
        if (isMutedInternal) {
            stopTension();
        }
    };

    /**
     * Retorna si el motor se encuentra silenciado actualmente.
     * @returns {boolean}
     */
    const isMuted = () => isMutedInternal;

    /**
     * Reproduce un beep de conteo regresivo (3, 2, 1).
     * 3 y 2 a 440 Hz (La4); 1 a 880 Hz (La5, agudo penetrante).
     * 
     * @param {number} countNumber - 3, 2 o 1.
     */
    const playCountBeep = (countNumber) => {
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }

        const now = ctx.currentTime;
        const frequency = countNumber === 1 ? 880 : 440;
        const duration = countNumber === 1 ? 0.35 : 0.28;

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(frequency, now);

        gain.gain.setValueAtTime(0.001, now);
        gain.gain.exponentialRampToValueAtTime(0.45, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + duration + 0.02);
    };

    /**
     * Inicia un zumbido/drone de tensión oscilante modulado en baja frecuencia (60-85 Hz).
     */
    const playTension = () => {
        if (isMutedInternal) return;
        stopTension(); // Asegurar detener instancia previa si existía

        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }

        const now = ctx.currentTime;

        try {
            tensionOscillator = ctx.createOscillator();
            tensionOscillator.type = 'triangle';
            tensionOscillator.frequency.setValueAtTime(75, now);

            // Modulación LFO sutil para generar sensación de tensión creciente
            tensionLfo = ctx.createOscillator();
            tensionLfo.type = 'sine';
            tensionLfo.frequency.setValueAtTime(3.5, now); // 3.5 Hz palpitación

            const lfoGain = ctx.createGain();
            lfoGain.gain.setValueAtTime(15, now); // Modula frecuencia +- 15 Hz

            tensionLfo.connect(lfoGain);
            lfoGain.connect(tensionOscillator.frequency);

            tensionFilterNode = ctx.createBiquadFilter();
            tensionFilterNode.type = 'lowpass';
            tensionFilterNode.frequency.setValueAtTime(160, now);

            tensionGainNode = ctx.createGain();
            tensionGainNode.gain.setValueAtTime(0.001, now);
            tensionGainNode.gain.exponentialRampToValueAtTime(0.3, now + 0.5);

            tensionOscillator.connect(tensionFilterNode);
            tensionFilterNode.connect(tensionGainNode);
            tensionGainNode.connect(ctx.destination);

            tensionOscillator.start(now);
            tensionLfo.start(now);
        } catch {
            // Manejo silencioso ante errores de audio
        }
    };

    /**
     * Detiene de forma limpia el zumbido de tensión oscilante.
     */
    const stopTension = () => {
        try {
            if (tensionGainNode && audioContext) {
                const now = audioContext.currentTime;
                tensionGainNode.gain.cancelScheduledValues(now);
                tensionGainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
            }

            setTimeout(() => {
                if (tensionOscillator) {
                    try { tensionOscillator.stop(); tensionOscillator.disconnect(); } catch {}
                    tensionOscillator = null;
                }
                if (tensionLfo) {
                    try { tensionLfo.stop(); tensionLfo.disconnect(); } catch {}
                    tensionLfo = null;
                }
                if (tensionFilterNode) {
                    try { tensionFilterNode.disconnect(); } catch {}
                    tensionFilterNode = null;
                }
                if (tensionGainNode) {
                    try { tensionGainNode.disconnect(); } catch {}
                    tensionGainNode = null;
                }
            }, 90);
        } catch {
            tensionOscillator = null;
            tensionLfo = null;
            tensionGainNode = null;
            tensionFilterNode = null;
        }
    };

    /**
     * Reproduce la señal de disparo/trigger penetrante a 1200 Hz ("¡FRENTE!") con ataque instantáneo.
     */
    const playTrigger = () => {
        stopTension();
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }

        const now = ctx.currentTime;

        // Tono principal penetrante 1200 Hz (onda de choque aguda)
        const toneOsc = ctx.createOscillator();
        const toneGain = ctx.createGain();

        toneOsc.type = 'sawtooth';
        toneOsc.frequency.setValueAtTime(1200, now);
        toneOsc.frequency.exponentialRampToValueAtTime(400, now + 0.18);

        toneGain.gain.setValueAtTime(0.7, now);
        toneGain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

        toneOsc.connect(toneGain);
        toneGain.connect(ctx.destination);

        // Capa de impacto percusivo (ruido blanco ultracorto)
        try {
            const bufferSize = Math.floor(ctx.sampleRate * 0.12);
            const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const outputData = noiseBuffer.getChannelData(0);
            for (let i = 0; i < bufferSize; i++) {
                outputData[i] = Math.random() * 2 - 1;
            }

            const noiseSource = ctx.createBufferSource();
            noiseSource.buffer = noiseBuffer;

            const noiseFilter = ctx.createBiquadFilter();
            noiseFilter.type = 'lowpass';
            noiseFilter.frequency.setValueAtTime(5000, now);
            noiseFilter.frequency.exponentialRampToValueAtTime(200, now + 0.1);

            const noiseGain = ctx.createGain();
            noiseGain.gain.setValueAtTime(0.8, now);
            noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

            noiseSource.connect(noiseFilter);
            noiseFilter.connect(noiseGain);
            noiseGain.connect(ctx.destination);

            noiseSource.start(now);
            noiseSource.stop(now + 0.13);
        } catch {}

        toneOsc.start(now);
        toneOsc.stop(now + 0.23);
    };

    /**
     * Reproduce una fanfarria triunfal de victoria con acordes mayores ascendentes (C5, E5, G5, C6).
     */
    const playVictory = () => {
        stopTension();
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }

        const now = ctx.currentTime;
        const notes = [
            { freq: 523.25, timeOffset: 0.00, duration: 0.18 }, // C5
            { freq: 659.25, timeOffset: 0.12, duration: 0.18 }, // E5
            { freq: 783.99, timeOffset: 0.24, duration: 0.22 }, // G5
            { freq: 1046.50, timeOffset: 0.38, duration: 0.65 } // C6 (agudo brillante sostenido)
        ];

        notes.forEach((note) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            osc.type = 'triangle';
            osc.frequency.setValueAtTime(note.freq, now + note.timeOffset);

            const startTime = now + note.timeOffset;
            gain.gain.setValueAtTime(0.001, startTime);
            gain.gain.exponentialRampToValueAtTime(0.4, startTime + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + note.duration);

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start(startTime);
            osc.stop(startTime + note.duration + 0.02);
        });
    };

    /**
     * Reproduce el tono disonante y descendente de falta temprana / salida falsa (sawtooth 320 Hz -> 80 Hz).
     */
    const playFalseStart = () => {
        stopTension();
        if (isMutedInternal) return;
        const ctx = getContext();
        if (!ctx) return;
        if (ctx.state === 'suspended') {
            ctx.resume().catch(() => {});
        }

        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(320, now);
        osc.frequency.linearRampToValueAtTime(80, now + 0.32);

        gain.gain.setValueAtTime(0.001, now);
        gain.gain.exponentialRampToValueAtTime(0.5, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.32);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.34);
    };

    /**
     * Libera de forma segura todos los recursos del motor de audio.
     */
    const dispose = () => {
        stopTension();
        if (audioContext) {
            try {
                audioContext.close();
            } catch {}
            audioContext = null;
        }
    };

    return {
        unlock,
        playCountBeep,
        playTension,
        stopTension,
        playTrigger,
        playVictory,
        playFalseStart,
        setMuted,
        isMuted,
        dispose
    };
};

// =========================================================================
// 3. SCREEN WAKE LOCK API HELPER
// =========================================================================

let screenWakeLockSentinel = null;
let wakeLockVisibilityHandler = null;

/**
 * Solicita el bloqueo de apagado de pantalla (Screen Wake Lock) con re-adquisición automática ante cambios de visibilidad.
 * 
 * @returns {Promise<boolean>} Verdadero si el bloqueo fue activado exitosamente.
 */
export const requestScreenWakeLock = async () => {
    try {
        if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) {
            return false;
        }

        if (screenWakeLockSentinel && !screenWakeLockSentinel.released) {
            return true;
        }

        screenWakeLockSentinel = await navigator.wakeLock.request('screen');

        if (!wakeLockVisibilityHandler && typeof document !== 'undefined') {
            wakeLockVisibilityHandler = async () => {
                if (document.visibilityState === 'visible' && (!screenWakeLockSentinel || screenWakeLockSentinel.released)) {
                    try {
                        screenWakeLockSentinel = await navigator.wakeLock.request('screen');
                    } catch {}
                }
            };
            document.addEventListener('visibilitychange', wakeLockVisibilityHandler);
        }

        return true;
    } catch {
        return false;
    }
};

/**
 * Libera de forma segura el bloqueo de apagado de pantalla (Screen Wake Lock).
 * 
 * @returns {Promise<boolean>} Verdadero si fue liberado.
 */
export const releaseScreenWakeLock = async () => {
    try {
        if (wakeLockVisibilityHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', wakeLockVisibilityHandler);
            wakeLockVisibilityHandler = null;
        }

        if (screenWakeLockSentinel) {
            await screenWakeLockSentinel.release();
            screenWakeLockSentinel = null;
            return true;
        }
        return false;
    } catch {
        screenWakeLockSentinel = null;
        return false;
    }
};

// =========================================================================
// 4. FEEDBACK HÁPTICO (NAVIGATOR.VIBRATE)
// =========================================================================

/**
 * Ejecuta vibración háptica en dispositivos móviles compatibles.
 * 
 * @param {number|Array<number>|string} [patternOrType=20] - Tipo semántico ('trigger' | 'tap' | 'fault' | 'victory') o duración en ms.
 * @returns {boolean} Verdadero si la vibración fue invocada.
 */
export const triggerHapticFeedback = (patternOrType = 20) => {
    try {
        const nav = typeof window !== 'undefined' && window.navigator && typeof window.navigator.vibrate === 'function'
            ? window.navigator
            : (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function' ? navigator : null);

        if (!nav) return false;

        if (typeof patternOrType === 'string') {
            const normalized = patternOrType.toLowerCase().trim();
            switch (normalized) {
                case 'trigger':
                    return nav.vibrate(40); // 40ms penetrante
                case 'tap':
                    return nav.vibrate(20); // 20ms toque suave
                case 'fault':
                    return nav.vibrate([60, 40, 60, 40, 100]);
                case 'victory':
                    return nav.vibrate([40, 50, 40, 50, 120]);
                default:
                    return nav.vibrate(20);
            }
        }

        if (Array.isArray(patternOrType) || typeof patternOrType === 'number') {
            return nav.vibrate(patternOrType);
        }

        return false;
    } catch {
        return false;
    }
};

// =========================================================================
// 5. PRECARGA DE ASSETS MULTIMEDIA EN SEGUNDO PLANO
const preloadedImagesCache = new Map();

/**
 * Normaliza y resuelve la URL completa de un asset multimedia del tema.
 * 
 * @param {string} mediaPath - Ruta relativa o URL absoluta.
 * @returns {string} URL absoluta normalizada.
 */
export const resolveAssetUrl = (mediaPath) => {
    if (!mediaPath || typeof mediaPath !== 'string') return '';
    const trimmed = mediaPath.trim();
    if (
        trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('blob:') ||
        trimmed.startsWith('data:')
    ) {
        return trimmed;
    }

    const rawApi = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';
    const backendOrigin = rawApi.replace(/\/api\/?$/i, '');
    const normalizedPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `${backendOrigin}${normalizedPath}`;
};

/**
 * Precarga en segundo plano todos los recursos gráficos y sprites del tema activo.
 * Se invoca una única vez al cargar el tema visual, cacheando resultados para evitar descargas duplicadas.
 * 
 * @param {Object} theme - Objeto de datos del tema con URLs de sprites y fondos.
 * @returns {Promise<boolean>} Promesa resuelta cuando todos los recursos han sido cacheados.
 */
export const preloadThemeAssets = async (theme) => {
    if (!theme || typeof theme !== 'object' || typeof window === 'undefined') {
        return false;
    }

    const rawUrls = [
        theme.backgroundImageUrl,
        theme.logoImageUrl,
        theme.spriteBackUrl || '/assets/quick-reaction/cowboy-back.png',
        theme.spriteFrontUrl || '/assets/quick-reaction/cowboy-front.png',
        theme.spriteShootUrl,
        theme.spriteVictoryUrl,
        theme.banner1536Url,
        theme.banner1920Url
    ].filter((url) => typeof url === 'string' && url.trim().length > 0);

    const urlsToPreload = rawUrls.map(resolveAssetUrl);

    const loadPromises = urlsToPreload.map((url) => {
        return new Promise((resolve) => {
            if (preloadedImagesCache.has(url)) {
                resolve(true);
                return;
            }

            const img = new Image();
            img.onload = () => {
                preloadedImagesCache.set(url, img);
                resolve(true);
            };
            img.onerror = () => {
                preloadedImagesCache.set(url, false); // Recordar fallo para no reintentar
                resolve(false);
            };
            img.src = url;
        });
    });

    try {
        await Promise.all(loadPromises);
        return true;
    } catch {
        return false;
    }
};

// =========================================================================
// 6. DETERMINACIÓN DE INSIGNIAS OFICIALES POR TIEMPO EN MS
// =========================================================================

/**
 * Retorna la información completa de la insignia (Badge) ganada por el aficionado según su tiempo de reacción.
 * 
 * @param {number} reactionTimeMs - Tiempo en milisegundos.
 * @param {boolean} [isEarlyFault=false] - Si cometió falta temprana / salida en falso.
 * @param {boolean} [isBelowHumanThreshold=false] - Si fue menor al umbral humano (< 120 ms).
 * @param {boolean} [isTimeout=false] - Si se agotó el tiempo de inactividad.
 * @returns {{ name: string, title: string, tier: string, color: string, icon: string, description: string }} Insignia determinada.
 */
export const getReactionBadge = (reactionTimeMs, isEarlyFault = false, isBelowHumanThreshold = false, isTimeout = false) => {
    if (isEarlyFault || isBelowHumanThreshold) {
        return {
            name: '⚠️ Salida Falsa / Descalificado',
            title: 'Salida Falsa',
            tier: 'fault',
            color: '#ff416c',
            icon: 'bi-exclamation-triangle-fill',
            description: 'Presionaste antes de la señal o por debajo del umbral fisiológico humano.'
        };
    }

    if (isTimeout) {
        return {
            name: '🛑 A Paso de Tortuga / Tiempo Agotado',
            title: 'Tiempo Agotado',
            tier: 'timeout',
            color: '#9e9e9e',
            icon: 'bi-hourglass-bottom',
            description: 'Se agotó el tiempo límite para reaccionar al disparo.'
        };
    }

    const ms = Number(reactionTimeMs) || 0;

    if (ms < 200) {
        return {
            name: '⚡ Nivel Divino / Ojo de Halcón',
            title: 'Nivel Divino',
            tier: 'god',
            color: '#00ffcc',
            icon: 'bi-lightning-charge-fill',
            description: '¡Reflejos sobrehumanos! Velocidad legendaria e instantánea.'
        };
    }

    if (ms >= 200 && ms <= 280) {
        return {
            name: '🐆 Reflejos Felinos',
            title: 'Reflejos Felinos',
            tier: 'elite',
            color: '#ffb703',
            icon: 'bi-fire',
            description: '¡Velocidad de élite! Reacción digna de un atleta profesional.'
        };
    }

    if (ms >= 281 && ms <= 380) {
        return {
            name: '🤠 Pistolero Veloz',
            title: 'Pistolero Veloz',
            tier: 'fast',
            color: '#00d2ff',
            icon: 'bi-award-fill',
            description: '¡Excelente desenfunde! Más rápido que el promedio general.'
        };
    }

    if (ms >= 381 && ms <= 500) {
        return {
            name: '🐢 Reflejo Humano',
            title: 'Reflejo Humano',
            tier: 'normal',
            color: '#70e000',
            icon: 'bi-person-check-fill',
            description: 'Buen intento dentro del rango estándar de velocidad humana.'
        };
    }

    return {
        name: '🛑 A Paso de Tortuga / Tiempo Agotado',
        title: 'Paso de Tortuga',
        tier: 'slow',
        color: '#fb8500',
        icon: 'bi-speedometer',
        description: 'Un poco lento, ¡sigue practicando para mejorar tu tiempo!'
    };
};

// =========================================================================
// 7. TELEMETRÍA DUAL (BACKEND + GOOGLE TAG MANAGER)
// =========================================================================

/**
 * Registra eventos de telemetría y pasos del embudo de conversión simultáneamente
 * en el Backend de Interaktia y en el Google Tag Manager (dataLayer).
 * 
 * @param {Object} params - Parámetros del evento de telemetría.
 * @param {string} params.roomId - GUID de la sala.
 * @param {string} params.sessionId - Identificador de sesión.
 * @param {string} params.stepName - Nombre del paso (ej. 'FORM_VIEW', 'FORM_SUBMIT', 'GAME_START', 'PLAY_SUBMIT', 'RESULT_VIEW').
 * @param {string} [params.source='mobile_fan'] - Fuente de tráfico.
 * @param {Object} [params.additionalData={}] - Datos analíticos adicionales para GTM.
 */
export const trackDualTelemetry = async ({ roomId, sessionId, stepName, source = 'mobile_fan', additionalData = {} }) => {
    try {
        if (!roomId || !stepName) return;

        // 1. Envío al Backend de Interaktia
        DLTrackTelemetry({
            roomId: roomId,
            sessionId: sessionId || 'session_' + Math.random().toString(36).substring(2, 10),
            stepName: stepName,
            source: source
        }).catch(() => {});

        // 2. Google Tag Manager / dataLayer push
        if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'personal_quick_reaction_step',
                step: stepName,
                roomId: roomId,
                sessionId: sessionId,
                timestamp: new Date().toISOString(),
                ...additionalData
            });
        }
    } catch {
        // Telemetría no bloqueante
    }
};

// =========================================================================
// 8. GESTIÓN DE SESIÓN PERSISTENTE LOCAL
// =========================================================================

/**
 * Obtiene la sesión activa almacenada de un participante en el dispositivo.
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 * @returns {Object|null} Datos de la sesión o null si no existe.
 */
export const getParticipantSession = (roomId) => {
    try {
        if (typeof localStorage === 'undefined' || !roomId) return null;
        const raw = localStorage.getItem(`${STORAGE_SESSION_KEY}_${roomId}`);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

/**
 * Guarda los datos de sesión del participante localmente.
 * 
 * @param {Object} sessionData - Datos con participantId, oneTimeToken, participantName, roomId, etc.
 */
export const saveParticipantSession = (sessionData) => {
    try {
        if (typeof localStorage === 'undefined' || !sessionData?.roomId) return;
        localStorage.setItem(`${STORAGE_SESSION_KEY}_${sessionData.roomId}`, JSON.stringify(sessionData));
    } catch {}
};

/**
 * Borra la sesión local del participante para permitir que otro aficionado juegue en el mismo dispositivo (Shared Device).
 * 
 * @param {string} roomId - Identificador GUID de la sala.
 */
export const clearParticipantSession = (roomId) => {
    try {
        if (typeof localStorage === 'undefined' || !roomId) return;
        localStorage.removeItem(`${STORAGE_SESSION_KEY}_${roomId}`);
    } catch {}
};

// =========================================================================
// 9. WRAPPERS DE NEGOCIO PARA OPERACIONES DL
// =========================================================================

export const GetPublicConfigBL = DLGetPublicConfig;
export const RegisterParticipantBL = DLRegisterParticipant;
export const SubmitPlayBL = DLSubmitPlay;
export const GetRankingBL = DLGetRanking;
export const GetWinnerBL = DLGetWinner;
export const GetAdminDataBL = DLGetAdminData;
export const UpdateAdminConfigBL = DLUpdateAdminConfig;
export const UpdateAdminOverlayBL = DLUpdateAdminOverlay;
export const UploadAssetBL = DLUploadAsset;
export const GetThemesBL = DLGetThemes;
export const SaveThemeBL = DLSaveTheme;
export const ResetParticipantBL = DLResetParticipant;
export const FullResetBL = DLFullReset;

export default {
    sanitizeInputText,
    sanitizeRoomId,
    validateParticipantName,
    validateEmail,
    validatePhone,
    validateTerms,
    validatePrivacy,
    validateRegistrationForm,
    createSynthesizedAudioEngine,
    requestScreenWakeLock,
    releaseScreenWakeLock,
    triggerHapticFeedback,
    preloadThemeAssets,
    getReactionBadge,
    trackDualTelemetry,
    getParticipantSession,
    saveParticipantSession,
    clearParticipantSession,
    // DL Wrappers
    GetPublicConfigBL,
    RegisterParticipantBL,
    SubmitPlayBL,
    GetRankingBL,
    GetWinnerBL,
    GetAdminDataBL,
    UpdateAdminConfigBL,
    UpdateAdminOverlayBL,
    UploadAssetBL,
    GetThemesBL,
    SaveThemeBL,
    ResetParticipantBL,
    FullResetBL
};
